<!--
  ZoneTable part — the toolbar: the search field, the Filters trigger, the always-visible inline
  filter pills, and the `#toolbar-actions` slot. INTERNAL: not a Zone component, no story, not in
  llms.txt. Rendered only by Table.vue.

  Presentational. Search echoes through `update:search-input` on every keystroke and the table owns
  the debounce, so this part never holds a value the table would have to read back out of it.
-->

<template>
  <!--
    `@container`: the breakpoint is the TOOLBAR's width, not the viewport's. A table in a narrow
    column next to a sidebar is as cramped as one on a phone, and a viewport `md:` would keep it on
    one row with the pills spilling over the Filters button.
  -->
  <div
    class="@container flex flex-wrap items-center gap-3 border-b border-gray-200 px-3.5 py-2.5"
    data-testid="table-toolbar"
  >
    <!--
      Under 640px of toolbar the row stacks the way Zone::DataTable's does on a phone: the search
      box takes its own full-width row, the Filters trigger the next, and the trailing group (inline
      pills, `#toolbar-actions`) a left-aligned row under them instead of hugging the right edge.
    -->
    <div
      v-if="hasSearchSide"
      class="flex w-full min-w-0 flex-wrap items-center gap-2 @min-[640px]:w-auto @min-[640px]:flex-nowrap"
    >
      <div
        v-if="searchable"
        class="w-full @min-[640px]:w-[240px] @min-[640px]:shrink-0"
        data-testid="table-search"
      >
        <ZoneSearchInput
          size="small"
          :model-value="searchInput"
          :placeholder="searchPlaceholder"
          @update:model-value="$emit('update:searchInput', $event)"
        />
      </div>

      <TableFilters
        v-if="hasFilterSurface"
        :surface="filterSurface"
        :open="filtersOpen"
        :filters="surfaceFilters"
        :values="values"
        :active-count="activeFilterCount"
        :active-badge="activeFilterBadge"
        :set-filter="setFilter"
        :reset="reset"
        @update:open="$emit('update:filtersOpen', $event)"
        @reset="$emit('reset')"
        @set="(key, next) => $emit('set', key, next)"
      >
        <template
          v-for="name in forwardedSlots"
          :key="name"
          #[name]="slotProps"
        >
          <slot
            :name="name"
            v-bind="slotProps ?? {}"
          />
        </template>
      </TableFilters>
    </div>

    <!--
      Figma's toolbar guidance: "when no filter and search are needed at the toolbar, spread out
      the right hand content so that the left doesn't remain empty". With nothing on the left the
      trailing group takes the whole row and distributes itself instead of hugging the edge.
    -->
    <div :class="trailingClasses">
      <template
        v-for="filter in inlineFilters"
        :key="filter.key"
      >
        <!--
          A `toggle` carries no `options`, so `pillItems()` is empty for it and a pill group would
          render nothing at all. Same control the filter surface uses for this type (see
          FilterFields.vue) — Zone has no switch component. `label` is ZoneCheckbox's ADJACENT
          text, which is the accessible name for a checkbox, not the uppercase field row.
        -->
        <ZoneCheckbox
          v-if="filter.type === 'toggle'"
          :label="filter.label"
          :model-value="toggleFilterValue(filter)"
          :data-testid="`inline-filter-${filter.key}`"
          @update:model-value="$emit('set', filter.key, asToggleValue($event))"
        />
        <ZonePillGroup
          v-else
          size="md"
          :multiple="filter.type !== 'select'"
          :items="pillItems(filter)"
          :loading="filter.loading"
          :model-value="filter.type === 'select' ? singleFilterValue(filter) : filterValue(filter)"
          :data-testid="`inline-filter-${filter.key}`"
          @update:model-value="filter.type === 'select'
            ? $emit('set', filter.key, asSingleValue($event))
            : $emit('set', filter.key, $event)"
        />
      </template>

      <slot name="toolbar-actions" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, useSlots, type PropType } from 'vue';
import ZoneCheckbox from '../Checkbox.vue';
import ZonePillGroup from '../PillGroup.vue';
import ZoneSearchInput from '../SearchInput.vue';
import TableFilters from './Filters.vue';
import * as filterHelpers from './filters';
import type { TableFilter, TableFilterValue, TableRowKey } from './types';

defineOptions({ name: 'ZoneTableToolbar' });

const props = defineProps({
  searchable: {
    type: Boolean,
    required: true,
  },
  /** The field's own value, not the committed query — Table.vue owns the debounce. */
  searchInput: {
    type: String,
    required: true,
  },
  searchPlaceholder: {
    type: String,
    required: true,
  },
  /** Filters behind the trigger, and the ones pinned into this row as pills. */
  surfaceFilters: {
    type: Array as PropType<TableFilter[]>,
    required: true,
  },
  inlineFilters: {
    type: Array as PropType<TableFilter[]>,
    required: true,
  },
  values: {
    type: Object as PropType<Record<string, TableFilterValue[]>>,
    required: true,
  },
  hasFilterSurface: {
    type: Boolean,
    required: true,
  },
  filterSurface: {
    type: String as PropType<'drawer' | 'panel'>,
    required: true,
  },
  filtersOpen: {
    type: Boolean,
    required: true,
  },
  activeFilterCount: {
    type: Number,
    required: true,
  },
  activeFilterBadge: {
    type: String,
    required: true,
  },
  setFilter: {
    type: Function as PropType<(key: string, values: unknown) => void>,
    required: true,
  },
  reset: {
    type: Function as PropType<() => void>,
    required: true,
  },
});

defineEmits(['update:searchInput', 'update:filtersOpen', 'reset', 'set']);

const slots = useSlots();

/** Whether the left-hand group has anything in it at all. */
const hasSearchSide = computed<boolean>(() => props.searchable || props.hasFilterSurface);

// Figma's toolbar guidance: with nothing on the left, the trailing content spreads across the
// row rather than hugging the right edge and leaving a dead half.
const trailingClasses = computed<string>(() => (hasSearchSide.value
  ? 'flex w-full flex-wrap items-center gap-2 @min-[640px]:ml-auto @min-[640px]:w-auto'
  : 'flex w-full flex-1 flex-wrap items-center justify-between gap-2'));

/** Relays the `#filters` slot on to the surface. See Table.vue's `forwardedSlots`. */
const forwardedSlots = computed<string[]>(() => Object.keys(slots));

function filterValue(filter: TableFilter): TableFilterValue[] {
  return filterHelpers.filterValue(props.values, filter);
}

function singleFilterValue(filter: TableFilter): TableFilterValue | null {
  return filterHelpers.singleFilterValue(props.values, filter);
}

function pillItems(filter: TableFilter): { id: TableRowKey; label: string }[] {
  return filterHelpers.pillItems(filter);
}

function toggleFilterValue(filter: TableFilter): boolean {
  return filterHelpers.toggleFilterValue(props.values, filter);
}

function asSingleValue(value: unknown): TableFilterValue[] {
  return filterHelpers.asSingleValue(value);
}

function asToggleValue(checked: boolean): TableFilterValue[] {
  return filterHelpers.asToggleValue(checked);
}
</script>
